import * as Sentry from "@sentry/browser";
import { SplitFactory } from "@splitsoftware/splitio";

import featureFlags from "../featureFlags";
import { getEnv } from "../getEnv";

const DEFAULT_CLIENT_KEY = "DEFAULT_USER_ID";
const SPLIT_IO_AUTH_KEY = getEnv("SPLIT_IO_AUTH_KEY");
const TREATMENT_ENABLED = "on";

// List of splits. Putting it here to:
// 1. prevent typos
// 2. have overview of used splits in one place
// Needs to be extended: SplitName = "AP_ENABLE_CONTRIBUTOR" | "AP_ENABLE_PROJECT_VIEW"
type SplitName = "AP_ENABLE_TEST";

const FACTORY_CONFIG = {
  core: {
    authorizationKey: SPLIT_IO_AUTH_KEY ?? "",
    // the key can be the logged in
    // user id, or the account id that
    // the logged in user belongs to.
    // The type of customer (user, account, custom)
    // is chosen during Split's sign-up process.
    key: DEFAULT_CLIENT_KEY,
    trafficType: "user",
  },
  startup: {
    readyTimeout: 1.5, // 1.5 sec
  },
};

const _getFactory = (): SplitIO.ISDK | undefined => {
  if ((SPLIT_IO_AUTH_KEY ?? "") === "") {
    // eslint-disable-next-line no-console
    console.error("SPLIT_IO_AUTH_KEY is not set");
    return;
  }
  return SplitFactory(FACTORY_CONFIG);
};

export const _factory = _getFactory();
export let _client: SplitIO.IClient | undefined = _factory?.client();

let _clientKey = DEFAULT_CLIENT_KEY;
let _membershipId = "";

export const updateClientKey = (newClientKey: string): void => {
  if (newClientKey === _clientKey) {
    return;
  }
  _clientKey = newClientKey;
  _client = _factory?.client(newClientKey);
};

export const updateOrganizationId = (newMembershipId: string): void => {
  _membershipId = newMembershipId;
};

export const getTreatment = async (
  splitName: SplitName,
  extraAttributes: SplitIO.Attributes = {}
): Promise<string | undefined> => {
  // If we do not assign _client to local variable,
  // it will use the latest client instead of client value at calling time!
  // (after ready() is resolved and when calling getTreatment())
  const client = _client;
  if (!client) {
    const extraContext = { sdkAuthKey: SPLIT_IO_AUTH_KEY, splitName };
    const error = new Error("Split.IO client was not initialized");
    if (featureFlags.isSentryEnabled) {
      Sentry.captureException(error, { extra: extraContext });
    }
    throw error;
  }
  const attributes: SplitIO.Attributes = {
    organization: _membershipId,
    ...extraAttributes,
  };
  await client.ready();
  return client.getTreatment(splitName, attributes);
};

export const isEnabled = async (
  flagName: SplitName,
  extraAttributes: SplitIO.Attributes = {}
): Promise<boolean> => {
  return (await getTreatment(flagName, extraAttributes))?.toLowerCase() === TREATMENT_ENABLED;
};

export default { updateClientKey, updateOrganizationId, getTreatment, isEnabled };
